import type { Trailer } from "../providers/vehiclesProvider";
import { Create, EditBase, Resource, Tab, useRedirect } from "react-admin";
import { TrailerForm } from "../details/vehicles/TrailerForm";
import { BackofficeRecordTitle } from "../misc/BackofficeRecordTitle";
import { useAutovioContext } from "../hooks/useAutovioContext";
import { useEffect } from "react";
import { Box } from "@mui/material";
import { Column } from "../misc/Column";
import { overviewCardStyle } from "../misc/backofficeTheme";
import { Tabs } from "../misc/Tabs";
import { VehicleCalendar } from "../details/vehicles/VehicleCalendar";
import { grants } from "../backoffice.access_control";
import { VehicleDetailsHeader } from "../details/vehicles/VehicleDetailsHeader";
import { useRecordAndSwitchDrivingSchoolIfNecessary } from "../hooks/useRecordAndSwitchDrivingSchoolIfNecessary";
import { AccessDenied } from "../misc/AccessDenied";
import { LoadingIndicator } from "../misc/LoadingIndicator";
import { DefaultList } from "../misc/DefaultList";
import { VehicleDataCard } from "../details/vehicles/VehicleDataCard";

function CreateTrailerForm() {
  const [{ drivingSchoolId }] = useAutovioContext();
  const redirect = useRedirect();
  useEffect(() => {
    if (!grants.has("Vehicle:create") && drivingSchoolId) {
      redirect(`/drivingSchools/${drivingSchoolId}/fleet#trailers`);
    }
  }, []);

  if (!grants.has("Vehicle:create")) {
    return <AccessDenied />;
  }

  if (!drivingSchoolId) {
    return (
      <Box className="centered">
        <Column sx={{ alignItems: "center" }}>Bitte wähle eine Fahrschule aus.</Column>
      </Box>
    );
  }

  return (
    <Create<Trailer>
      title={<BackofficeRecordTitle />}
      mutationOptions={{
        onSuccess: ({ name }) => {
          // eslint-disable-next-line react-compiler/react-compiler
          window.onNavigation = () => {
            delete window.onNavigation;
            showSuccessToast(`Anhänger ${name} erfolgreich gespeichert`);
          };
          redirect(`/drivingSchools/${drivingSchoolId}/fleet#trailers`);
        },
      }}
    >
      <TrailerForm mode="create" sx={{ padding: overviewCardStyle.padding }} />
    </Create>
  );
}

function TrailerDetails() {
  const redirect = useRedirect();
  const [{ drivingSchoolId }] = useAutovioContext();
  const trailer = useRecordAndSwitchDrivingSchoolIfNecessary<Trailer>((it) => it.drivingSchoolId);

  if (!trailer) {
    return <LoadingIndicator />;
  }

  return (
    <EditBase
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          showSuccessToast("Änderungen erfolgreich gespeichert");
          redirect(`/drivingSchools/${drivingSchoolId}/fleet#trailers`);
        },
      }}
    >
      <Tabs
        sx={{ ...overviewCardStyle, padding: { xs: "5px 16px 16px", sm: "10px 30px" } }}
        header={<VehicleDetailsHeader />}
        mobileSelectAriaLabel="Fahrzeugbereich"
        syncWithLocation={false}
        syncWithHash
        separateCards
      >
        <Tab label="Kalender" value="calendar">
          <VehicleCalendar />
        </Tab>
        {grants.has("Vehicle:edit") && (
          <Tab label="Anhänger-Daten" value="data">
            <VehicleDataCard>
              <TrailerForm mode="edit" />
            </VehicleDataCard>
          </Tab>
        )}
      </Tabs>
    </EditBase>
  );
}

export const trailersResource = (
  <Resource
    key="trailers"
    name="trailers"
    list={DefaultList}
    create={CreateTrailerForm}
    edit={TrailerDetails}
    options={{
      label: "Anhänger",
      createTitle: "Neuer Anhänger",
      noEntriesText: "Noch kein Anhänger hinterlegt.",
      noMatchingEntriesText: "Keine passenden Anhänger gefunden.",
    }}
    recordRepresentation={(record: Trailer) => `${record.name} (${record.plateNumber})`}
  />
);
